/**
 * Copyright © Ircam amplify 2024
 */
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'
import { API } from '../../../api'
import { IampLogger } from '../../../utils/IampLogger'
import { UploadFileInfo } from '../UploadFileInfo'

export const KEY_REFERENCE_URL = 'referenceUrl'
export const KEY_SUSPECTED_URL = 'suspectedCovers'
export const KEY_SUSPECTED_SAMPLES = 'suspectedSamples'

const duploBackURL = import.meta.env.VITE_API_BASE_URI
const storageApiUrl = import.meta.env.VITE_STORAGE_API_URL

export const getToken = async () => {
  const clientId = import.meta.env.VITE_CLIENT_ID
  const clientSecret = import.meta.env.VITE_CLIENT_SECRET
  const authUrl = import.meta.env.VITE_AUTH_URL

  const payload = {
    client_id: clientId,
    client_secret: clientSecret,
    grant_type: 'client_credentials',
  }
  const resp = await axios.post(authUrl, payload, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
  return resp
}

/**
 * Request the processing of a Duplo module.
 * @param payload The payload of the API request.
 * @param api The name of the API.
 * @returns The API response of the request.
 */
export const requestJobProcessing = async (payload: string, api: string) => {
  const res = await API(await fetchAuthSession()).post(
    `${duploBackURL}/${api}`,
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  return res
}

/**
 * Request the processing of a Duplo module.
 * @param token The authentication token.
 * @param payload The payload of the API request.
 * @param api The name of the API.
 * @returns The API response of the request.
 */
export const requestJobProcessingWithToken = async (
  token: string,
  payload: string,
  api: string
) => {
  const resp = await axios.post(`${duploBackURL}/${api}`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  return resp
}

export const getJobReport = async (
  token: string,
  jobId: string,
  api: string
) => {
  const resp = await axios.get(`${duploBackURL}/${api}/${jobId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  return resp
}

export const getJobReportFromSession = async (jobId: string, api: string) => {
  if (api.endsWith('/')) {
    api = api.substring(0, api.length - 1)
  }

  const resp = await API(await fetchAuthSession()).get(
    `${duploBackURL}/${api}/${jobId}`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  return resp
}

export const calculateCost = async (
  iasPathsArray: string[],
  serviceName: string
) => {
  const payload = {
    ias_paths: iasPathsArray,
    service_name: serviceName,
  }
  IampLogger.debug('calculateCost Payload:', payload)
  const resp = await API(await fetchAuthSession()).post(
    `${duploBackURL}/job/calculate-cost`,
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  IampLogger.debug('calculateCost Response:', resp)
  return resp
}

export const createStorageCognito = async () => {
  const resp = await API(await fetchAuthSession())
    .post(
      `${storageApiUrl}manager/`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    .catch(() => {})
  return resp
}

export const createStorage = async (token: string) => {
  const resp = await axios
    .post(
      `${storageApiUrl}manager/`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    )
    .catch(() => {})
  return resp
}

export const uploadFileToIasCognito = async (
  file: UploadFileInfo,
  id: string
) => {
  // Check if localFile is defined
  if (!file.localFile) {
    throw new Error(
      'Local file is undefined. Please provide a valid file to upload.'
    )
  }

  const resp = await API(await fetchAuthSession()).put(
    `${storageApiUrl + id}/${file.localFile.name}`,
    file.localFile,
    {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    }
  )

  return resp
}

export const uploadFileToIas = async (
  token: string,
  file: File,
  id: string
) => {
  const resp = await axios.put(`${storageApiUrl + id}/${file.name}`, file, {
    headers: {
      'Content-Type': 'application/octet-stream',
      Authorization: `Bearer ${token}`,
    },
  })
  return resp
}

export const downloadExtFileToIas = async (
  token: string,
  id: string,
  fileLink: string
) => {
  const resp = await axios
    .put(
      `${storageApiUrl}manager/${id}?externalUrl=${fileLink}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .catch(() => {})
  return resp
}

/**
 * The response of Storage's get request.
 */
export interface StorageFileInfo {
  filename: string
  ias: string
  id: string
  source: string
  status: string
  [key: string]: any // This allows for additional properties with string keys
}

export const getStorageInfoCognito = async (id: string) => {
  const resp = await API(await fetchAuthSession()).get<StorageFileInfo>(
    `${storageApiUrl}manager/${id}`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  )

  return resp
}

export const getStorageInfo = async (token: string, id: string) => {
  const resp = await axios
    .get(`${storageApiUrl}manager/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    })
    .catch(() => {})

  return resp
}

export const getConsumptionKpi = async (token: string) => {
  const resp = await axios
    .get(`${duploBackURL}/company/kpis`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    })
    .catch(() => {})

  return resp
}

/**
 * Fetch for a file from the Storage module with Cognito authentication
 * and return its contents.
 *
 * @param iasId The ias ID, without 'ias://' prefix.
 * @param fileName The file name.
 * @returns A blob instance.
 */
export const downloadContentFromIasCognito = async (
  iasId: string,
  fileName: string
) => {
  const apiUrl = `${storageApiUrl}${iasId}/${fileName}`

  const response = await API(await fetchAuthSession()).get(apiUrl, {
    headers: {},
    responseType: 'blob',
  })

  return new Blob([response.data], {})
}

/**
 * Download a file from the Storage module with Cognito authentication
 * to the browser's directory.
 *
 * @param iasId The ias ID, without 'ias://' prefix.
 * @param fileName The file name.
 */
export const downloadIasFileCognito = async (
  iasId: string,
  fileName: string
) => {
  const blob = await downloadContentFromIasCognito(iasId, fileName)
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export const downloadIasFile = async (
  token: string,
  iasId: string,
  fileName: string
) => {
  try {
    const apiUrl = `${storageApiUrl}${iasId}/${fileName}`

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    })

    const blob = new Blob([response.data], {})

    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.style.display = 'none'

    document.body.appendChild(a)

    a.click()
    window.URL.revokeObjectURL(url)
    try {
      document.body.removeChild(a)
    } catch (e) {
      console.log('Error in downloadIasFile', e)
    }
    return response
  } catch (error) {
    return error
  }
}

export const readIasFile = async (
  token: string,
  iasId: string,
  fileName: string
) => {
  try {
    const apiUrl = `${storageApiUrl}${iasId}/${fileName}`

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    })

    const blob = new Blob([response.data], { type: 'audio/wav' })

    const url = window.URL.createObjectURL(blob)

    return url
  } catch (error) {
    return error
  }
}

export const formatMutlipleUrls = (string: string) => {
  const newString = string
    .replaceAll('"', '')
    .replaceAll('[', '')
    .replaceAll(']', '')
    .replaceAll(' ', '')

  const splittedStringArray = newString.split(',')
  if (
    splittedStringArray[splittedStringArray.length - 1] === '' ||
    splittedStringArray[splittedStringArray.length - 1] === undefined
  )
    splittedStringArray.pop()

  return splittedStringArray
}

export const formatOneUrl = (string: string) => {
  const newString = string
    .replaceAll('"', '')
    .replaceAll('[', '')
    .replaceAll(']', '')
    .replaceAll(' ', '')
    .replaceAll(',', '')

  return newString
}

export const formatDate = (UTCdate: string) => {
  const dateOb = new Date(UTCdate)

  const date = `${
    dateOb.getFullYear() +
    `0${dateOb.getMonth() + 1}`.slice(-2) +
    `0${dateOb.getDate()}`.slice(-2)
  }_${`0${dateOb.getHours()}`.slice(-2)}h${`0${dateOb.getMinutes()}`.slice(
    -2
  )}m${`0${dateOb.getSeconds()}`.slice(-2)}`

  return date
}

export const secondsToMinutesSeconds = (seconds: number) => {
  const min = Math.floor(Math.trunc(seconds) / 60)
  const remainingSeconds = Math.trunc(seconds) % 60
  return `${min}:${remainingSeconds.toString().padStart(2, '0')}`
}

export const estimatedLoadingTimeMsg = (loadingTimeInSeconds: number) => {
  if (loadingTimeInSeconds <= 15) return 'Still a few seconds to wait......'
  if (loadingTimeInSeconds <= 60) return 'Less than a minute to wait...'
  if (loadingTimeInSeconds <= 120) return 'Less than two minutes to wait...'
  if (loadingTimeInSeconds <= 180) return 'Less than three minutes to wait...'
  if (loadingTimeInSeconds <= 240) return 'Less than four minutes to wait...'
  if (loadingTimeInSeconds <= 300) return 'Less than five minutes to wait...'
  if (loadingTimeInSeconds <= 360) return 'Less than six minutes to wait...'

  return 'still a few minutes to wait...'
}

export const loadingStatusMsg = (status: string) => (
  <p>
    <p className={status === 'pending' ? '' : 'inactive'}>
      Processing will start shortly...
    </p>

    <p className={status === 'downloading' ? '' : 'inactive'}>
      Downloading tracks...
    </p>

    <p className={status === 'running' ? '' : 'inactive'}>
      Processing tracks...
    </p>

    <p className={status === 'succeeded' ? '' : 'inactive'}>Done</p>
  </p>
)

export const getRandomNb = (min: number, max: number) =>
  Math.random() * (max - min) + min

// TODO: seems to be unused. Should removed.
export const errorMessage = (nOKField: string) => {
  let errorMsg
  switch (nOKField) {
    case 'format':
      errorMsg = "Sorry, there's a format error (must be .wav or .flac)."
      break
    case 'channels':
      errorMsg = "Sorry, there's a channel error (must be Stereo)."
      break
    case 'sample_rate[Hz]':
      errorMsg =
        "Sorry, there's a sample rate error (must be at least 44100 Hz)."
      break
    case 'resolution[bits]':
      errorMsg = "Sorry, there's a resolution error (must be at least 16 bits)."
      break
    case 'length[s]':
      errorMsg =
        "Sorry, there's a length error (must be between 0.5 s and 9 min 30 s)."
      break

    default:
      errorMsg = "Sorry, there's an error."
      break
  }
  return errorMsg
}

export const getDatesDiffAsDays = (
  startDate: Date | string,
  endDate?: Date | string
): number => {
  return Math.abs(
    (new Date(startDate).getTime() -
      (endDate === undefined ? new Date() : new Date(endDate)).getTime()) /
      (1000 * 3600 * 24)
  )
}

/**
 * Return the message corresponding to the given error code.
 * @param code The error code.
 * @returns The message in string.
 */
export function standardErrorMsg(code: number): string {
  switch (code) {
    case 415:
      return 'Unsupported format'
  }

  return 'Internal error'
}
